// components/order/OrderTable.jsx
import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Box,
    CircularProgress,
} from "@mui/material";
import Icon from "@/theme/vectors/bundle";
import { formatDateTime, formatCurrency, formatPhoneNumber } from "@/utils/order/helpers";
import { ORDER_STATUS_COLORS } from "@/utils/order/constants";

const OrderTable = ({
    orders,
    loading,
    onOrderClick,
    onStatusChange,
    translate,
}) => {
    // Hiển thị trạng thái đơn hàng với màu sắc tương ứng
    const renderStatus = (status) => {
        if (!status) return null;
        
        // Chuẩn hóa trạng thái để phù hợp với ORDER_STATUS_COLORS
        const normalizedStatus = status.toUpperCase();
        const color = ORDER_STATUS_COLORS[normalizedStatus] || "default";
        
        // Xác định trạng thái cần có chữ màu trắng
        const needsWhiteText = normalizedStatus === "CANCELLED" || normalizedStatus === "SHIPPING";
        
        return (
            <Chip
                label={translate(`order.statusOptions.${normalizedStatus.toLowerCase()}`)}
                color={color}
                variant="filled"
                size="small"
                sx={{ 
                    fontWeight: 500, 
                    ml: 1,
                    color: needsWhiteText ? '#fff' : undefined,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                }}
            />
        );
    };

    if (loading && (!orders || orders.length === 0)) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <Paper sx={{ p: 3, textAlign: "center" }}>
                {translate("order.noOrders")}
            </Paper>
        );
    }

    return (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell width="30%">
                                {translate("order.fields.orderId")}
                            </TableCell>
                            <TableCell>
                                {translate("order.fields.customerName")}
                            </TableCell>
                            <TableCell>
                                {translate("order.fields.customerPhone")}
                            </TableCell>
                            <TableCell align="right">
                                {translate("order.fields.totalAmount")}
                            </TableCell>
                            <TableCell align="center">
                                {translate("order.fields.createdAt")}
                            </TableCell>
                            <TableCell align="right">
                                {translate("common.actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((order, index) => {
                            // Không cần phải xử lý order_id/id vì API trả về id
                            // Sử dụng order.order_status trực tiếp vì API trả về trường order_status
                            return (
                                <TableRow
                                    hover
                                    key={order.id}
                                    sx={{ 
                                        cursor: "pointer",
                                        backgroundColor: index % 2 === 0 ? 'inherit' : 'rgba(0, 0, 0, 0.02)'
                                    }}
                                    onClick={() => onOrderClick(order.id)}
                                >
                                    <TableCell component="th" scope="row" sx={{ background: 'transparent !important' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {order.id}
                                            {renderStatus(order.order_status)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{order.customer_name}</TableCell>
                                    <TableCell>{formatPhoneNumber(order.customer_phone)}</TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(order.total_amount || 0)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatDateTime(order.created_at)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate("common.view")}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onOrderClick(order.id);
                                                }}
                                            >
                                                <Icon name="view" size={20} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress
                                        size={24}
                                        sx={{ my: 1 }}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default OrderTable;
